import { useCallback, useEffect, useRef, useState } from "react";
import AdminLayout from "../layouts/AdminLayout";
import { services } from "../services";
import { useAuth } from "../utils/hooks/useAuth";
import { isPPAdmin, isStateAdmin } from "../utils/roles";

type LearnerReportExportItem = {
  id: string;
  fileName: string;
  uploadDate: string;
  processingStatus: string;
  total: number | null;
  processedRows: number;
  processedAt: string | null;
  errorMessage: string | null;
  progressPercentage: number;
  resultFileAvailable: boolean;
};

const pollIntervalMs = 3000;
const maxPollDurationMs = 20 * 60 * 1000;

const formatProcessingStatus = (status: string) => {
  switch (status) {
    case "success":
      return "Ready";
    case "failed":
      return "Failed";
    case "processing":
      return "Processing";
    case "pending":
      return "Queued";
    default:
      return status || "Unknown";
  }
};

export function LearnerReportDownloadsContent() {
  const { role } = useAuth();
  const stateAdmin = isStateAdmin(role);
  const ppAdmin = isPPAdmin(role);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [vleCscId, setVleCscId] = useState("");
  const [placementPartnerId, setPlacementPartnerId] = useState("");
  const [placementPartners, setPlacementPartners] = useState<
    { id: string; name: string }[]
  >([]);
  const [isExporting, setIsExporting] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [lastExportId, setLastExportId] = useState<string | null>(null);
  const [currentExport, setCurrentExport] =
    useState<LearnerReportExportItem | null>(null);
  const [history, setHistory] = useState<LearnerReportExportItem[]>([]);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [pollingTimedOut, setPollingTimedOut] = useState(false);

  const pollerRef = useRef<number | null>(null);
  const pollStartRef = useRef<number | null>(null);

  const clearPolling = useCallback(() => {
    if (pollerRef.current) {
      window.clearInterval(pollerRef.current);
      pollerRef.current = null;
    }
  }, []);

  useEffect(() => clearPolling, [clearPolling]);

  const fetchHistory = useCallback(async () => {
    setIsHistoryLoading(true);

    try {
      const records = await services.requestSvc.get<LearnerReportExportItem[]>(
        "/learner-report-exports",
        {
          withCredentials: true,
        },
      );
      setHistory(records || []);
    } catch (error) {
      console.error("Failed to fetch learner report history", error);
      setHistory([]);
    } finally {
      setIsHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchHistory();
  }, [fetchHistory]);

  useEffect(() => {
    if (ppAdmin) {
      setPlacementPartners([]);
      return;
    }

    void services.requestSvc
      .get<Array<{ id: bigint; name: string }>>("/placement-partners", {
        withCredentials: true,
      })
      .then((records) => {
        setPlacementPartners(
          (records || []).map((record) => ({
            id: String(record.id),
            name: record.name,
          })),
        );
      })
      .catch((error) => {
        console.error("Failed to fetch placement partners", error);
        setPlacementPartners([]);
      });
  }, [ppAdmin]);

  const downloadResultFile = useCallback(async (id: string) => {
    const response = await services.requestSvc.get<{ url: string | null }>(
      `/learner-report-exports/${id}/download`,
      {
        withCredentials: true,
      },
    );

    if (response?.url) {
      window.open(response.url, "_blank", "noopener,noreferrer");
      return true;
    }

    setStatusMessage("Report file is not available yet.");
    return false;
  }, []);

  const pollExportStatus = useCallback(
    async (id: string) => {
      try {
        if (!pollStartRef.current) {
          pollStartRef.current = Date.now();
        }

        if (Date.now() - pollStartRef.current > maxPollDurationMs) {
          clearPolling();
          setPollingTimedOut(true);
          setIsExporting(false);
          setStatusMessage(
            "Export is taking longer than expected. You can refresh or check again later.",
          );
          return;
        }

        const exportStatus = await services.requestSvc.get<LearnerReportExportItem | null>(
          `/learner-report-exports/${id}`,
          {
            withCredentials: true,
          },
        );

        if (!exportStatus) {
          clearPolling();
          setIsExporting(false);
          setStatusMessage("Export request was not found.");
          return;
        }

        setCurrentExport(exportStatus);

        if (
          exportStatus.processingStatus === "success" &&
          exportStatus.resultFileAvailable
        ) {
          clearPolling();
          setPollingTimedOut(false);
          setIsExporting(false);
          setStatusMessage("Download is ready. Opening file...");
          await downloadResultFile(id);
          await fetchHistory();
          return;
        }

        if (exportStatus.processingStatus === "failed") {
          clearPolling();
          setPollingTimedOut(false);
          setIsExporting(false);
          setStatusMessage(
            exportStatus.errorMessage || "Learner report export failed.",
          );
          await fetchHistory();
          return;
        }
      } catch (error) {
        console.error("Failed to fetch learner report export status", error);
        clearPolling();
        setIsExporting(false);
        setStatusMessage("Failed to fetch export status.");
      }
    },
    [clearPolling, downloadResultFile, fetchHistory],
  );

  const startPolling = useCallback(
    (id: string) => {
      clearPolling();
      pollStartRef.current = Date.now();
      setPollingTimedOut(false);
      pollerRef.current = window.setInterval(() => {
        void pollExportStatus(id);
      }, pollIntervalMs);
      void pollExportStatus(id);
    },
    [clearPolling, pollExportStatus],
  );

  const handleExport = async () => {
    if (!startDate || !endDate) {
      setStatusMessage("Please select both start date and end date.");
      return;
    }

    if (endDate < startDate) {
      setStatusMessage("End date must be on or after start date.");
      return;
    }

    setIsExporting(true);
    setCurrentExport(null);
    setStatusMessage(
      "Downloading data... this may take some time. Please do not close the tab.",
    );

    try {
      const response = await services.requestSvc.post<{
        dumpRequestId: string;
      }>(
        "/learner-report-exports",
        {
          startDate,
          endDate,
          vleCscId: vleCscId.trim() || undefined,
          placementPartnerId: placementPartnerId || undefined,
        },
        {
          withCredentials: true,
        },
      );

      setLastExportId(response.dumpRequestId);
      await fetchHistory();
      startPolling(response.dumpRequestId);
    } catch (error) {
      console.error("Failed to queue learner report export", error);
      setIsExporting(false);
      setStatusMessage("Failed to start learner report export.");
    }
  };

  const currentProgress = currentExport?.progressPercentage || 0;

  return (
    <div className="max-w-5xl flex flex-col gap-6">
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          <h2 className="text-xl font-semibold text-gray-800">
            Learner Data Download
          </h2>
          <p className="text-sm text-gray-600 mt-2">
            Export complete learner records with course completion status for a
            selected date range.
          </p>
          {stateAdmin && (
            <p className="text-sm text-gray-600 mt-2">
              This export is restricted to learners registered under your
              assigned state.
            </p>
          )}
          {ppAdmin && (
            <p className="text-sm text-gray-600 mt-2">
              This export is restricted to learners mapped to your assigned
              placement partner.
            </p>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
            <label className="flex flex-col gap-2">
              <span className="text-sm font-medium text-gray-700">
                Start Date
              </span>
              <input
                type="date"
                className="input-primary"
                value={startDate}
                onChange={(event) => setStartDate(event.target.value)}
                disabled={isExporting}
              />
            </label>

            <label className="flex flex-col gap-2">
              <span className="text-sm font-medium text-gray-700">
                End Date
              </span>
              <input
                type="date"
                className="input-primary"
                value={endDate}
                min={startDate || undefined}
                onChange={(event) => setEndDate(event.target.value)}
                disabled={isExporting}
              />
            </label>
            <label className="flex flex-col gap-2">
              <span className="text-sm font-medium text-gray-700">
                VLE CSC ID
              </span>
              <input
                type="text"
                className="input-primary"
                value={vleCscId}
                onChange={(event) => setVleCscId(event.target.value)}
                placeholder="Optional VLE CSC ID"
                disabled={isExporting}
              />
            </label>
            {!ppAdmin && (
              <label className="flex flex-col gap-2">
                <span className="text-sm font-medium text-gray-700">
                  Placement Partner
                </span>
                <select
                  className="input-primary"
                  value={placementPartnerId}
                  onChange={(event) => setPlacementPartnerId(event.target.value)}
                  disabled={isExporting}
                >
                  <option value="">All Placement Partners</option>
                  {placementPartners.map((partner) => (
                    <option key={partner.id} value={partner.id}>
                      {partner.name}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>

          <div className="mt-6 flex flex-col gap-3">
            <button
              type="button"
              className="btn-primary w-full md:w-fit"
              onClick={handleExport}
              disabled={isExporting || !startDate || !endDate}
            >
              {isExporting ? "Downloading..." : "Download Learner Data"}
            </button>

            {statusMessage && (
              <div
                className={`text-sm ${
                  statusMessage.toLowerCase().includes("failed") ||
                  statusMessage.toLowerCase().includes("not found")
                    ? "text-red-600"
                    : "text-gray-700"
                }`}
              >
                {statusMessage}
              </div>
            )}

            {isExporting && (
              <div className="border border-blue-100 bg-blue-50 rounded-lg p-4">
                <div className="flex items-center justify-between gap-4 text-sm text-blue-900">
                  <span>{formatProcessingStatus(currentExport?.processingStatus || "pending")}</span>
                  <span>{currentProgress}%</span>
                </div>
                <div className="mt-3 h-2 w-full rounded-full bg-blue-100 overflow-hidden">
                  <div
                    className="h-full bg-blue transition-all duration-300"
                    style={{ width: `${currentProgress}%` }}
                  />
                </div>
                <div className="mt-2 text-xs text-blue-900">
                  {currentExport?.total
                    ? `${currentExport.processedRows} of ${currentExport.total} records processed`
                    : "Preparing your learner export"}
                </div>
              </div>
            )}

            {pollingTimedOut && lastExportId && (
              <button
                type="button"
                className="btn-secondary w-full md:w-fit"
                onClick={() => startPolling(lastExportId)}
                disabled={isExporting}
              >
                Retry Status Check
              </button>
            )}
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-800">
                Export History
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Previously generated learner report files can be downloaded from
                here while the signed link is available.
              </p>
            </div>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => void fetchHistory()}
              disabled={isHistoryLoading}
            >
              {isHistoryLoading ? "Refreshing..." : "Refresh"}
            </button>
          </div>

          <div className="mt-6 overflow-x-auto">
            {isHistoryLoading ? (
              <div className="text-sm text-gray-500">Loading export history...</div>
            ) : history.length === 0 ? (
              <div className="text-sm text-gray-500">
                No learner report exports found yet.
              </div>
            ) : (
              <table className="min-w-full text-sm border border-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-3 py-2 border-b">File Name</th>
                    <th className="text-left px-3 py-2 border-b">
                      Requested At
                    </th>
                    <th className="text-left px-3 py-2 border-b">Progress</th>
                    <th className="text-left px-3 py-2 border-b">Status</th>
                    <th className="text-left px-3 py-2 border-b">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((item) => (
                    <tr key={item.id}>
                      <td className="px-3 py-2 border-b">{item.fileName}</td>
                      <td className="px-3 py-2 border-b">
                        {new Date(item.uploadDate).toLocaleString()}
                      </td>
                      <td className="px-3 py-2 border-b">
                        <div className="min-w-[160px]">
                          <div className="flex justify-between text-xs text-gray-600 mb-1">
                            <span>{item.processedRows}</span>
                            <span>{item.progressPercentage}%</span>
                          </div>
                          <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                            <div
                              className="h-full bg-blue transition-all duration-300"
                              style={{
                                width: `${item.progressPercentage}%`,
                              }}
                            />
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2 border-b">
                        <div>{formatProcessingStatus(item.processingStatus)}</div>
                        {item.errorMessage && (
                          <div className="text-xs text-red-600 mt-1">
                            {item.errorMessage}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 border-b">
                        <button
                          type="button"
                          className="btn-secondary"
                          disabled={!item.resultFileAvailable}
                          onClick={() => void downloadResultFile(item.id)}
                        >
                          Download
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
  );
}

export default function LearnerReportDownloadsPage() {
  return (
    <AdminLayout title="Learner Reports">
      <LearnerReportDownloadsContent />
    </AdminLayout>
  );
}
